"use client";

import { useState, useEffect } from "react";

interface FormBlockProps {
  data: {
    form_uuid: string;
    heading?: string;
    description?: string;
  };
}

export function FormBlock({ data }: FormBlockProps) {
  const [formConfig, setFormConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data.form_uuid) {
      // Fetch form configuration
      const apiUrl = `http://${window.location.hostname}:8000/api/website/forms/${data.form_uuid}`;
      fetch(apiUrl)
        .then((res) => res.json())
        .then((resData) => {
          if (resData.form) {
            setFormConfig(resData.form);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching form:", err);
          setError("Failed to load form configuration.");
          setLoading(false);
        });
    }
  }, [data.form_uuid]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const apiUrl = `http://${window.location.hostname}:8000/api/website/forms/${data.form_uuid}/submit`;
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const resData = await res.json();

      if (res.ok) {
        setSuccess(resData.message || "Form submitted successfully!");
        setFormData({});
      } else {
        setError(resData.message || "Validation error or server error.");
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(
        "An error occurred while submitting the form. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-500">Loading form...</div>
    );
  }

  if (!formConfig) {
    return (
      <div className="py-12 text-center text-red-500">Form not found.</div>
    );
  }

  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900 rounded-3xl my-12 max-w-3xl mx-auto">
      {(data.heading || data.description) && (
        <div className="text-center mb-10">
          {data.heading && (
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {data.heading}
            </h2>
          )}
          {data.description && (
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {data.description}
            </p>
          )}
        </div>
      )}

      {success ? (
        <div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 p-6 rounded-xl text-center">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p className="font-medium text-lg">{success}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-4 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-5">
            {formConfig.fields?.map((fieldBlock: any, index: number) => {
              const field = fieldBlock.data;
              const type = fieldBlock.type;

              if (type === "text") {
                return (
                  <div key={index}>
                    <label
                      htmlFor={field.name}
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type={field.type || "text"}
                      name={field.name}
                      id={field.name}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      className="input-tenant"
                    />
                  </div>
                );
              }

              if (type === "textarea") {
                return (
                  <div key={index}>
                    <label
                      htmlFor={field.name}
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <textarea
                      name={field.name}
                      id={field.name}
                      required={field.required}
                      placeholder={field.placeholder}
                      rows={4}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      className="input-tenant"
                    />
                  </div>
                );
              }

              if (type === "select") {
                return (
                  <div key={index}>
                    <label
                      htmlFor={field.name}
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <select
                      name={field.name}
                      id={field.name}
                      required={field.required}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      className="input-tenant"
                    >
                      <option value="">Select an option</option>
                      {field.options?.map((opt: any, i: number) => (
                        <option key={i} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              return null;
            })}
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full"
            >
              {submitting
                ? "Submitting..."
                : formConfig.submit_button_label || "Submit"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
